/**
 * @file ScopeSelector.jsx
 * @description Scope selection component with search functionality
 *
 * @version 1.0.0
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {Form, InputGroup, Spinner} from "react-bootstrap";
import {ChevronDown, ChevronUp, ChevronRight, User, Globe, Users, MonitorPlay} from "lucide-react";

import "./TopNav.css";
import Search from "@assets/icons/Search.svg?react";
import {NOTIFICATION_TYPES, useNotifications} from "@context/NotificationContext.jsx";
import {errorMessage} from "@utils/apiHelpers";

// Function to recursively search through groups and their subgroups
const filterTree = (groups, searchTerm) => {
    if (!groups || !searchTerm.trim()) return {filteredGroups: groups, matchExpandedIds: new Set()};

    const term = searchTerm.toLowerCase();
    const matchExpandedIds = new Set();

    const filterRecursively = (group) => {
        const groupMatches = group.name.toLowerCase().includes(term);

        // Recursively filter subgroups
        let filteredSubgroups = [];
        if (group.subgroups && group.subgroups.length > 0) {
            filteredSubgroups = group.subgroups
                .map(sub => filterRecursively(sub))
                .filter(Boolean);
        }

        // Include this node if it matches or has matching descendants
        if (groupMatches || filteredSubgroups.length > 0) {
            // If a descendant matched, this node needs to be expanded
            if (filteredSubgroups.length > 0) {
                matchExpandedIds.add(group.id);
            }
            return {
                ...group,
                subgroups: filteredSubgroups
            };
        }

        return null;
    };

    const filteredGroups = groups.map(g => filterRecursively(g)).filter(Boolean);
    return {filteredGroups, matchExpandedIds};
};

const findScopeById = (groups, id) => {
    if (!groups || !id) return null;
    for (const group of groups) {
        if (group.id === id) return group;
        const match = findScopeById(group.subgroups, id);
        if (match) return match;
    }
    return null;
};

const GroupItem = ({group, level = 0, selectedScope, onSelect, expandedIds}) => {
    const [expanded, setExpanded] = useState(() => {
        return expandedIds?.has(group.id) ?? false;
    });

    // Sync expanded state when expandedIds changes (e.g., during search)
    useEffect(() => {
        if (expandedIds?.has(group.id)) {
            setExpanded(true);
        }
    }, [expandedIds, group.id]);

    const hasSubgroups = group.subgroups && group.subgroups.length > 0;

    const getTypeIcon = (type) => {
        switch (type) {
            case 'NCO':
                return <Globe size={16} className="me-2"/>;
            case 'partner':
                return <Users size={16} className="me-2"/>;
            case 'customer':
                return <User size={16} className="me-2"/>;
            case 'demo':
                return <MonitorPlay size={16} className="me-2"/>;
            default:
                return <Globe size={16} className="me-2"/>;
        }
    };

    const handleSelect = () => {
        const scopeObj = {
            id: group.id,
            name: group.name,
            type: group.type,
            path: group.path,
            partner_id: group.partner_id,
            pathList: group.pathList,
            subgroups: group.subgroups
        };

        onSelect(scopeObj);
    };

    const toggleExpand = (e) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    return (
        <div className="group-item-container">
            <div
                className={`group-item ${level > 0 ? `level-${level}` : ''} ${selectedScope?.id === group.id ? 'active' : ''}`}
                onClick={handleSelect}
            >
                <div className="group-item-content">
                    {getTypeIcon(group.type)}
                    <span className="group-name">{group.name}</span>

                    {hasSubgroups && (
                        <span
                            className="expand-toggle"
                            onClick={toggleExpand}
                        >
                            {expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                        </span>
                    )}
                </div>
            </div>

            {hasSubgroups && expanded && (
                <div className="subgroups">
                    {group.subgroups.map(subgroup => (
                        <GroupItem
                            key={subgroup.id}
                            group={subgroup}
                            level={level + 1}
                            selectedScope={selectedScope}
                            onSelect={onSelect}
                            expandedIds={expandedIds}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ScopeSelector = ({
                           updateTokenAndFetch,
                           selectedScope,
                           setSelectedScope,
                           forceDemoScope,
                           setScopes,
                           scopes,
                           scopesLoading,
                           setScopesLoading,
                           demo_scope = null,
                           view_as_partner_id = null,
                           refreshKey = 0,
                       }) => {
    const {addNotification} = useNotifications();

    const [showScopePanel, setShowScopePanel] = useState(false);

    // Search related states
    const [searchTerm, setSearchTerm] = useState("");

    const scopeRef = useRef(null);
    const scopeTriggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const getGroupsRef = useRef(null);
    const lastRefreshKeyRef = useRef(refreshKey);
    const selectedScopeRef = useRef(selectedScope);

    useEffect(() => {
        selectedScopeRef.current = selectedScope;
    }, [selectedScope]);

    // Compute expanded IDs from selectedScope path (for non-search mode)
    const selectedPathIds = useMemo(() => {
        const ids = new Set();
        if (selectedScope?.pathList) {
            selectedScope.pathList.forEach(ancestor => ids.add(ancestor.id));
        }
        return ids;
    }, [selectedScope]);

    // Compute filtered tree and expanded IDs for search
    const {displayGroups, displayExpandedIds} = useMemo(() => {
        if (!searchTerm.trim()) {
            return {displayGroups: scopes, displayExpandedIds: selectedPathIds};
        }

        const {filteredGroups, matchExpandedIds} = filterTree(scopes, searchTerm);
        return {displayGroups: filteredGroups, displayExpandedIds: matchExpandedIds};
    }, [searchTerm, scopes, selectedPathIds]);

    const handleClickOutside = useCallback((event) => {
        if (scopeRef.current && !scopeRef.current.contains(event.target) &&
            !scopeTriggerRef.current?.contains(event.target)) {
            setShowScopePanel(false);
        }
    }, []);

    useEffect(() => {
        if (showScopePanel) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showScopePanel, handleClickOutside]);

    const getGroups = useCallback(async (preserveSelection = false) => {
        if (!scopesLoading) setScopesLoading(true);
        const params = view_as_partner_id ? `?partner_id=${view_as_partner_id}` : ""

        try {
            const res = await updateTokenAndFetch(`/groups${params}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });
            if (res.ok) {
                const data = await res.json();
                const processGroups = (groups, parentPath = []) => {
                    return groups.map(group => {
                        const processedGroup = {
                            ...group,
                            pathList: [...parentPath]
                        };

                        let groupName = group.name
                        if (group.id === 'NCO') {
                            groupName = demo_scope ? 'Global' : 'NetCyberOps'
                            processedGroup['name'] = groupName
                        }

                        if (group.subgroups && group.subgroups.length > 0) {
                            processedGroup.subgroups = processGroups(
                                group.subgroups,
                                [...parentPath, {
                                    id: group.id,
                                    name: groupName,
                                    type: group.type,
                                    path: group.path,
                                    partner_id: group.partner_id,
                                    pathList: processedGroup.pathList
                                }]
                            );
                        }

                        return processedGroup;
                    });
                };

                const processedGroups = processGroups(data.groups);
                const nextScopes = demo_scope ? [demo_scope, ...processedGroups] : processedGroups;
                if (!preserveSelection || !findScopeById(nextScopes, selectedScopeRef.current?.id)) {
                    setSelectedScope(processedGroups[0]);
                }
                setScopes(nextScopes);
            } else {
                console.error("Failed to fetch groups");
                const data = await res.json().catch(() => ({}));
                addNotification(`Error retrieving groups: ${errorMessage(data, res)}`, NOTIFICATION_TYPES.ERROR, "scope-groups");
            }
        } catch (error) {
            console.error("Error fetching groups:", error);
            addNotification(`Error retrieving groups: ${error}`, NOTIFICATION_TYPES.ERROR, "scope-groups");
        } finally {
            setScopesLoading(false);
        }
    }, [updateTokenAndFetch, scopesLoading, addNotification]);

    useEffect(() => {
        if (!scopesLoading && scopes === null) {
            getGroups();
        }
    }, [scopesLoading, getGroups, scopes]);

    useEffect(() => {
        getGroupsRef.current = getGroups;
    }, [getGroups]);

    useEffect(() => {
        if (refreshKey === lastRefreshKeyRef.current) return;
        lastRefreshKeyRef.current = refreshKey;
        getGroupsRef.current(true);
    }, [refreshKey]);

    // Focus search input when panel opens
    useEffect(() => {
        if (showScopePanel && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 100);
        }
    }, [showScopePanel]);

    const handleScopeSelect = (scope) => {
        if (forceDemoScope) return
        if (scope.id === selectedScope?.id) return;

        const scopeObj = {
            id: scope.id,
            name: scope.name,
            type: scope.type,
            path: scope.path,
            partner_id: scope.partner_id,
            pathList: scope.pathList,
            subgroups: scope.subgroups
        };

        setSelectedScope(scopeObj);
        setShowScopePanel(false);
        setSearchTerm("");
    };

    const toggleScopePanel = () => {
        if (!showScopePanel) setSearchTerm("");
        setShowScopePanel(!showScopePanel);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // const clearSearch = () => {
    //     setSearchTerm("");
    //     if (searchInputRef.current) {
    //         searchInputRef.current.focus();
    //     }
    // };

    return (
        <div className="scope-selector" ref={scopeTriggerRef}>
            <span className="scope-name d-flex align-items-center">
                {selectedScope ?
                    selectedScope.pathList && selectedScope.pathList.length > 0 ? (
                        [...selectedScope.pathList, selectedScope].map((group, index, arr) => (
                            <>
                                <div
                                    key={index}
                                    className={`scope-path-item${forceDemoScope ? "" : " clickable"}`}
                                    onClick={() => handleScopeSelect(group)}
                                >
                                    {group.name}
                                </div>
                                {index < arr.length - 1 && <div className="scope-path-separator">/</div>}
                            </>
                        ))
                    ) : (
                        <div
                            className={`scope-path-item${forceDemoScope ? "" : " clickable"}`}
                            onClick={() => handleScopeSelect(selectedScope)}
                        >
                            {selectedScope.name}
                        </div>
                    ) : ""
                }
            </span>
            {!forceDemoScope && (
                <span className={`scope-selector-arrow`} onClick={toggleScopePanel}>
                {showScopePanel ?
                    <ChevronUp size={16} className="ms-2"/> :
                    <ChevronDown size={16} className="ms-2"/>
                }
            </span>
            )}
            {showScopePanel && (
                <div
                    className="scope-panel-wrapper"
                    ref={scopeRef}
                >
                    <div className="scope-panel">

                        {/* Search input */}
                        <div className="scope-search-container">
                            <InputGroup>
                                <InputGroup.Text className="scope-search-icon">
                                    <Search size={14}/>
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search scopes..."
                                    aria-label="Search scopes"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    ref={searchInputRef}
                                    className="scope-search-input"
                                />
                            </InputGroup>
                        </div>

                        {scopesLoading ? (
                            <div className="scope-loading">
                                <Spinner animation="border" size="sm"/>
                                <span className="ms-2">Loading scopes...</span>
                            </div>
                        ) : displayGroups && displayGroups.length > 0 ? (
                            <div className="scope-tree">
                                {displayGroups.map(group => (
                                    <GroupItem
                                        key={group.id}
                                        group={group}
                                        selectedScope={selectedScope}
                                        onSelect={handleScopeSelect}
                                        expandedIds={displayExpandedIds}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="no-search-results">No matches found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScopeSelector;